// utm.js
//
// WGS84 lon/lat <-> UTM, in the browser.
//
// Why this is needed: the editing core works in a linear "planning frame" and
// exports UTM easting/northing -- that is what ToolbarComponent's pixelToGeo
// returns today (read from the GeoTIFF's projected CRS) and what the backend
// expects. The TIFF path gets UTM for free from the raster's affine transform.
// The KML path has only lon/lat, so it must do the projection itself.
//
//   [ADR: docs/20260826_satellite_kml_planning_frame.html]
//
// Standard Transverse Mercator series (Snyder / USGS Professional Paper 1395),
// accurate to a few millimetres inside a zone -- far better than needed for
// field planning. Verified against Python's `utm` package in _test_utm.mjs.

const A = 6378137.0;              // WGS84 semi-major axis
const F = 1 / 298.257223563;      // flattening
const K0 = 0.9996;                // UTM scale factor on the central meridian
const E2 = F * (2 - F);           // first eccentricity squared
const EP2 = E2 / (1 - E2);        // second eccentricity squared

const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

export function zoneFor(lon, lat) {
  // Normalise into [-180, 180) FIRST. Without this, lon exactly +180 yields
  // floor(360/6)+1 = 61, and there is no UTM zone 61 -- the projection would
  // then be computed against a central meridian that does not exist.
  const wrapped = ((((lon + 180) % 360) + 360) % 360) - 180;
  let zone = Math.floor((wrapped + 180) / 6) + 1;
  if (zone > 60) zone = 60;
  if (zone < 1) zone = 1;
  // Two long-standing exceptions to the regular 6-degree grid.
  if (lat >= 56 && lat < 64 && wrapped >= 3 && wrapped < 12) zone = 32;
  if (lat >= 72 && lat < 84) {
    if (wrapped >= 0 && wrapped < 9) zone = 31;
    else if (wrapped >= 9 && wrapped < 21) zone = 33;
    else if (wrapped >= 21 && wrapped < 33) zone = 35;
    else if (wrapped >= 33 && wrapped < 42) zone = 37;
  }
  return zone;
}

// Latitude band letter, as `utm.from_latlon` reports it (e.g. 15 + "T").
export function bandFor(lat) {
  const bands = 'CDEFGHJKLMNPQRSTUVWXX';
  if (lat < -80 || lat > 84) return null;
  return bands[Math.floor((lat + 80) / 8)];
}

export const centralMeridian = (zone) => (zone - 1) * 6 - 180 + 3;

// Shortest signed angular difference, in degrees, wrapped to [-180, 180).
// Essential near the antimeridian: lon 180 against zone 1 (CM -177) is -3
// degrees the short way, not +357. Feeding 357 into the Transverse Mercator
// series produces a meaningless easting in the billions.
const wrapDeg = (d) => ((((d + 180) % 360) + 360) % 360) - 180;

export function fromLonLat(lon, lat, forcedZone = null) {
  const zone = forcedZone || zoneFor(lon, lat);
  const phi = toRad(lat);
  const dLon = toRad(wrapDeg(lon - centralMeridian(zone)));

  const N = A / Math.sqrt(1 - E2 * Math.sin(phi) ** 2);
  const T = Math.tan(phi) ** 2;
  const C = EP2 * Math.cos(phi) ** 2;
  const Aa = Math.cos(phi) * dLon;

  // Meridional arc.
  const M = A * (
    (1 - E2 / 4 - (3 * E2 ** 2) / 64 - (5 * E2 ** 3) / 256) * phi
    - ((3 * E2) / 8 + (3 * E2 ** 2) / 32 + (45 * E2 ** 3) / 1024) * Math.sin(2 * phi)
    + ((15 * E2 ** 2) / 256 + (45 * E2 ** 3) / 1024) * Math.sin(4 * phi)
    - ((35 * E2 ** 3) / 3072) * Math.sin(6 * phi)
  );

  const easting = K0 * N * (
    Aa + ((1 - T + C) * Aa ** 3) / 6
    + ((5 - 18 * T + T ** 2 + 72 * C - 58 * EP2) * Aa ** 5) / 120
  ) + 500000.0;

  let northing = K0 * (M + N * Math.tan(phi) * (
    Aa ** 2 / 2 + ((5 - T + 9 * C + 4 * C ** 2) * Aa ** 4) / 24
    + ((61 - 58 * T + T ** 2 + 600 * C - 330 * EP2) * Aa ** 6) / 720
  ));
  if (lat < 0) northing += 10000000.0; // southern-hemisphere false northing

  return { easting, northing, zone, band: bandFor(lat), northern: lat >= 0 };
}

export function toLonLat(easting, northing, zone, northern = true) {
  const x = easting - 500000.0;
  const y = northern ? northing : northing - 10000000.0;

  const M = y / K0;
  const mu = M / (A * (1 - E2 / 4 - (3 * E2 ** 2) / 64 - (5 * E2 ** 3) / 256));
  const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));

  const phi1 = mu
    + ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu)
    + ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu)
    + ((151 * e1 ** 3) / 96) * Math.sin(6 * mu)
    + ((1097 * e1 ** 4) / 512) * Math.sin(8 * mu);

  const N1 = A / Math.sqrt(1 - E2 * Math.sin(phi1) ** 2);
  const T1 = Math.tan(phi1) ** 2;
  const C1 = EP2 * Math.cos(phi1) ** 2;
  const R1 = (A * (1 - E2)) / Math.pow(1 - E2 * Math.sin(phi1) ** 2, 1.5);
  const D = x / (N1 * K0);

  const lat = phi1 - ((N1 * Math.tan(phi1)) / R1) * (
    D ** 2 / 2
    - ((5 + 3 * T1 + 10 * C1 - 4 * C1 ** 2 - 9 * EP2) * D ** 4) / 24
    + ((61 + 90 * T1 + 298 * C1 + 45 * T1 ** 2 - 252 * EP2 - 3 * C1 ** 2) * D ** 6) / 720
  );

  const dLonOut = (
    D - ((1 + 2 * T1 + C1) * D ** 3) / 6
    + ((5 - 2 * C1 + 28 * T1 - 3 * C1 ** 2 + 8 * EP2 + 24 * T1 ** 2) * D ** 5) / 120
  ) / Math.cos(phi1);

  // Wrap so a zone-1 / zone-60 result never emerges as e.g. -183 degrees.
  return { lon: wrapDeg(centralMeridian(zone) + toDeg(dLonOut)), lat: toDeg(lat) };
}
